import { CTBProgram } from "../../../utils/api/endpoints/programs/parsePrograms";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import { useGetProgramStatsQuery, useDeleteProgramMutation } from "../../../utils/api/endpoints/programsApi";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import DeleteConfirmationModal from "../../multi_tenant/DeleteConfirmationModal";
import toast from "react-hot-toast";
import { isProgramDraft, isScopeSnapshotPending, isScopePendingVerification } from "../../../utils/programs/statusUtils";
import { motion, AnimatePresence } from "framer-motion";

// Utility function to format the program type
const formatProgramType = (type?: string): string => {
  return type === "PTAAS" ? "PTaaS" : type || "";
};

const ProgramCard = ({
  program,
  className,
  onClick
}: {
  program: CTBProgram;
  className?: string;
  onClick?: () => void;
}) => {
  const { role } = useUserCredentials();
  const userRoleNum = role ? Number(role) : 0;
  const isAdminRole = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(userRoleNum as UserRole);
  const isResearcher = userRoleNum === UserRole.RESEARCHER;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [deleteProgram, { isLoading: isDeleting }] = useDeleteProgramMutation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    // Use capture phase so clicks with stopPropagation still close the menu
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);

  // Valid-only severity stats per program (call unconditionally; skip if no id/title)
  const { data: sev } = useGetProgramStatsQuery(
    { programId: Number(program?.id), validOnly: true },
    { skip: !program?.id || !program?.title }
  );

  // Determine delete permissions
  const now = Date.now();
  const startTs = program?.expected_start_date
    ? new Date(program.expected_start_date).getTime()
    : NaN;
  const notStarted = isNaN(startTs) || startTs > now;
  const canAdminDelete = role === UserRole.ADMIN;
  const canBusinessDelete = role === UserRole.BUSINESS && !program?.isActivated && notStarted;
  const canShowDelete = (canAdminDelete || canBusinessDelete) && !program?.isDelete;

  // Determine edit permissions (same logic as ActionButtons.tsx)
  const canEdit =
    (role === UserRole.ADMIN || role === UserRole.SUB_ADMIN || role === UserRole.ADMIN_MANAGER) ||
    (!program?.isActivated && !program?.isDelete && (role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR || role === UserRole.BUSINESS_MANAGER));

  // Admin-like role check to simplify conditional menu rendering
  const isAdminLike = userRoleNum === UserRole.ADMIN || userRoleNum === UserRole.SUB_ADMIN || userRoleNum === UserRole.ADMIN_MANAGER;

  const handleDelete = async () => {
    if (!program?.id || isDeleting) return;
    try {
      await deleteProgram(Number(program.id)).unwrap();
      toast.success("Program deleted successfully!");
      setMenuOpen(false);
    } catch (err) {
      toast.error("Failed to delete the program.");
    }
  };

  // Safety check - don't render if program is invalid
  if (!program || !program.title) {
    return null;
  }

  // Time-based progress badge: Completed / In Progress / Not Started
  const progress = (() => {
    const now = Date.now();
    const start = program.expected_start_date
      ? new Date(program.expected_start_date).getTime()
      : NaN;
    const end = program.expected_end_date
      ? new Date(program.expected_end_date).getTime()
      : NaN;

    if (!isNaN(end) && end < now) return "Completed" as const;
    if (!isNaN(start) && start > now) return "Upcoming" as const;
    if (!isNaN(start) || !isNaN(end)) return "In Progress" as const;
    return undefined;
  })();

  const formatDate = (ds?: string) => {
    if (!ds) return "N/A";
    const d = new Date(ds);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
  };

  const plainDescription = (() => {
    const html = program.description || "";
    const text = String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const words = text.split(" ");
    return words.length > 50 ? words.slice(0, 50).join(" ") + " …" : text;
  })();

  return (
    <article
      className={`relative flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md ${className}`}
    >
      <div className="flex-1">
        {/* Top row: chips and menu */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {program.isDelete ? (
              <span className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 5L15 15" stroke="#DC2626" stroke-width="2" stroke-linecap="round" />
                  <path d="M15 5L5 15" stroke="#DC2626" stroke-width="2" stroke-linecap="round" />
                </svg>
                Deleted
              </span>
            ) : (
              <>
                {(() => {
                  const architectureVersion = program.architecture_version || 1;
                  if (architectureVersion < 2) return null;

                  if (program.scheduling_type === 'continuous') {
                    return (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-purple-50 px-2.5 py-1 text-[10px] font-bold text-purple-700 border border-purple-100 uppercase tracking-wider">
                        Continuous
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 border border-emerald-100 uppercase tracking-wider">
                      One-off
                    </span>
                  );
                })()}
                {progress && (
                  <span
                    className={
                      "rounded-full px-3 py-1 text-xs font-semibold " +
                      (progress === "Completed"
                        ? "bg-green-100 text-green-700"
                        : progress === "In Progress"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-700")
                    }
                  >
                    {progress}
                  </span>
                )}
                {program.isActivated ? (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.6667 5.83301L8.33333 14.1663L5 10.833" stroke="#1D4ED8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Active
                  </span>
                ) : isProgramDraft(program) ? (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 border border-amber-200">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12V10" stroke="#B45309" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M10 16H10.01" stroke="#B45309" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      <circle cx="10" cy="10" r="8" stroke="#B45309" stroke-width="2" />
                    </svg>
                    Draft
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 5L15 15" stroke="#374151" stroke-width="2" stroke-linecap="round" />
                      <path d="M15 5L5 15" stroke="#374151" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    Inactive
                  </span>
                )}
              </>
            )}
          </div>
          {/* 3-dots menu */}
          <div className={"relative"} ref={menuRef}>
            <button
              aria-label="program-menu"
              className="h-6 w-6 rounded-full text-slate-500 hover:text-slate-400"
              onClick={e => {
                e.stopPropagation();
                setMenuOpen(o => !o);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="4" r="2" fill="currentColor" />
                <circle cx="10" cy="10" r="2" fill="currentColor" />
                <circle cx="10" cy="16" r="2" fill="currentColor" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_12px_30px_rgba(0,0,0,0.12),0_4px_14px_rgba(0,0,0,0.08)]">
                {canEdit && (
                  <button
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 bg-[#010D2C]"
                    onClick={e => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      navigate(`/dashboard/programs/${program.id}/edit`);
                    }}
                  >
                    <img src="/icons/edit-green.svg" alt="edit" />
                    <span className="text-sm font-medium text-[#58CC02]">Edit Program</span>
                  </button>
                )}
                {/* Only show extra menu items for non-admin roles */}
                {!isAdminLike && (
                  <>
                    <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50" onClick={e => { e.stopPropagation(); setMenuOpen(false); try { window.dispatchEvent(new CustomEvent("program:open-updates", { detail: program })); } catch { } }}>
                      <img src="/icons/updates.svg" alt="updates" />
                      <span className="text-sm text-slate-800">Recent Updates</span>
                    </button>
                    <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50" onClick={e => { e.stopPropagation(); setMenuOpen(false); try { window.dispatchEvent(new CustomEvent("program:open-info", { detail: program })); } catch { } }}>
                      <img src="/icons/program.svg" alt="info" />
                      <span className="text-sm text-slate-800">Program Information</span>
                    </button>
                    <button
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50"
                      onClick={e => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        try {
                          window.dispatchEvent(
                            new CustomEvent("program:open-stats", { detail: program })
                          );
                        } catch { }
                      }}
                    >
                      <img src="/icons/stats.svg" alt="stats" />
                      <span className="text-sm text-slate-800">Statistics</span>
                    </button>
                  </>
                )}
                {canShowDelete && (
                  <>
                    <div className="my-1 h-px bg-gray-100" />
                    <button
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-red-50 disabled:opacity-50"
                      onClick={e => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        setShowDeleteConfirm(true);
                      }}
                      disabled={isDeleting}
                    >
                      <img src="/icons/del.svg" alt="delete" />
                      <span className="text-sm font-medium text-red-600">Delete</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Status badges - new line for Action Required / Pending Scope */}
        <div className="mb-4 flex flex-wrap gap-2">
          {program.scheduling_type === 'continuous' && (program.runCount === 0) && !program.isDelete && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-[11px] font-bold text-green-600 border border-green-200 shadow-[0_0_15px_rgba(34,197,94,0.3)] animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              Action Required: Schedule Run
            </span>
          )}

          {isScopeSnapshotPending(program) && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-[11px] font-bold text-red-600 border border-red-200 shadow-[0_0_15px_rgba(220,38,38,0.3)] animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
              </span>
              Scope: Pending Review
            </span>
          )}

          {isAdminRole && isScopePendingVerification(program) && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold text-blue-600 border border-blue-200 shadow-[0_0_15px_rgba(37,99,235,0.3)] animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              Verify Scope
            </span>
          )}
        </div>

        {/* Company Name */}
        {(isAdminRole || isResearcher) && program.company_name && (
          <div className="mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100/50">
              {program.company_name}
            </span>
          </div>
        )}

        {/* Title */}
        <h3 className="mb-2 text-xl font-semibold text-slate-900">
          {program.title}
        </h3>

        {/* Description preview limited to ~50 words */}
        {program.description && (
          <p className="mb-4 line-clamp-4 text-sm leading-6 text-slate-700">
            {plainDescription}
          </p>
        )}

        {/* Dates */}
        <div className="mb-0 flex flex-col gap-2 text-sm font-extralight text-[#333333] ">
          <div className="flex items-center gap-2">
            <img src="/icons/start.svg" alt="start" />
            <span className="font-medium">Start:</span>
            <span>{formatDate(program.expected_start_date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <img src="/icons/end.svg" alt="end" />
            <span className="font-medium">End:</span>
            <span>{formatDate(program.expected_end_date)}</span>
          </div>
        </div>

        {/* Created by */}
        {program.creator && (
          <div className="mt-6 mb-4 flex items-center gap-2 text-medium text-slate-900">
            <img src="/icons/created.svg" alt="created by" />
            <span className="font-medium">Created by:</span>
            <span>{program.creator.displayName || program.creator.username || 'Unknown'}</span>
          </div>
        )}

        {/* Severity counts */}
        <div className="mb-4 mt-1 flex items-center justify-between text-xs w-full lg:text-[13px]">
          <span className="flex items-center gap-1 text-red-600">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L2 21H22L12 3Z" stroke="#DC2626" strokeWidth="2" strokeLinejoin="round" />
              <path d="M12 9V13" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.2" fill="#DC2626" />
            </svg>
            <span className="font-medium">{sev?.critical ?? 0}</span> <span className="hidden sm:inline">Critical</span>
          </span>
          <span className="flex items-center gap-1 text-orange-500">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L2 21H22L12 3Z" stroke="#F97316" strokeWidth="2" strokeLinejoin="round" />
              <path d="M12 9V13" stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.2" fill="#F97316" />
            </svg>
            <span className="font-medium">{sev?.high ?? 0}</span> <span className="hidden sm:inline">High</span>
          </span>
          <span className="flex items-center gap-1 text-yellow-600">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L2 21H22L12 3Z" stroke="#CA8A04" strokeWidth="2" strokeLinejoin="round" />
              <path d="M12 9V13" stroke="#CA8A04" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.2" fill="#CA8A04" />
            </svg>
            <span className="font-medium">{sev?.medium ?? 0}</span> <span className="hidden sm:inline">Medium</span>
          </span>
          <span className="flex items-center gap-1 text-green-600">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L2 21H22L12 3Z" stroke="#16A34A" strokeWidth="2" strokeLinejoin="round" />
              <path d="M12 9V13" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.2" fill="#16A34A" />
            </svg>
            <span className="font-medium">{sev?.low ?? 0}</span> <span className="hidden sm:inline">Low</span>
          </span>
          <span className="flex items-center gap-1 text-slate-500">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L2 21H22L12 3Z" stroke="#64748B" strokeWidth="2" strokeLinejoin="round" />
              <path d="M12 9V13" stroke="#64748B" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.2" fill="#64748B" />
            </svg>
            <span className="font-medium">{sev?.info ?? 0}</span> <span className="hidden sm:inline">Info</span>
          </span>
        </div>
      </div>

      {/* Footer actions - stick to bottom */}
      <div className="-mx-5 -mb-5 mt-auto flex items-center justify-end gap-6 rounded-b-xl bg-[#010D2C] p-4 text-sm">
        <button
          className="text-[#FFFFFF] underline-offset-2 hover:underline"
          onClick={e => {
            e.stopPropagation();
            navigate(`/dashboard/programs/${program.id}`);
          }}
        >
          Details
        </button>
        <button
          className="text-[#58CC02] underline-offset-2 hover:underline"
          onClick={e => {
            e.stopPropagation();
            navigate(
              `/dashboard/reports?programId=${encodeURIComponent(
                String(program.id)
              )}&programName=${encodeURIComponent(program.title)}`
            );
          }}
        >
          View Report
        </button>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          await handleDelete();
          setShowDeleteConfirm(false);
        }}
      />
    </article>
  );
};

export default ProgramCard;
